import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import { useSEO } from '@/hooks/useSEO';
import { getSEOConfigForPage } from '@/utils/seoUtils';
import TabbedVideoGallery from '@/components/ui/tabbed-video-gallery';
import FooterWithNewsletter from '@/components/ui/footer-column';
import { motion } from 'framer-motion';
import { Glow } from '@/components/ui/glow';
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface VideoItem {
  id: string;
  title: string;
  description: string;
  youtubeId: string;
  category: string;
  tags: string[];
  url?: string;
  url_slug?: string;
}

interface Category {
  name: string;
  tags: string[];
  description: string;
}

interface SettingsResponse {
  categories: Category[];
}

const GettingStartedPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get dynamic video data based on slug parameter
  const getDynamicVideoData = () => {
    // Only return dynamic data if we have a specific slug AND the video is found
    if (slugParam && apiVideos.length > 0) {
      const videoBySlug = apiVideos.find(video => video.url_slug === slugParam);
      if (videoBySlug) {
        // Extract video ID from various YouTube URL formats
        let videoId = videoBySlug.youtubeId;
        if (videoBySlug.youtubeId.includes('/embed/')) {
          videoId = videoBySlug.youtubeId.split('/embed/')[1]?.split('?')[0] || videoBySlug.youtubeId;
        } else if (videoBySlug.youtubeId.includes('youtube.com/watch?v=')) {
          videoId = videoBySlug.youtubeId.split('v=')[1]?.split('&')[0] || videoBySlug.youtubeId;
        } else if (videoBySlug.youtubeId.includes('youtu.be/')) {
          videoId = videoBySlug.youtubeId.split('youtu.be/')[1]?.split('?')[0] || videoBySlug.youtubeId;
        } else if (videoBySlug.youtubeId.includes('youtube.com/shorts/')) {
          videoId = videoBySlug.youtubeId.split('shorts/')[1]?.split('?')[0] || videoBySlug.youtubeId;
        }
        return {
          title: videoBySlug.title,
          description: videoBySlug.description,
          imageUrl: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        };
      }
    }
    
    // Return null if no specific slug - this will use page-level SEO
    return null;
  };

  const { fallbackMetadata } = getSEOConfigForPage('/getting-started');
  const { seoData, canonicalUrl } = useSEO({ fallbackMetadata });

  // API state
  const [apiVideos, setApiVideos] = useState<VideoItem[]>([]);
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [isLoadingVideos, setIsLoadingVideos] = useState(true);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  
  // URL parameter handling
  const slugParam = searchParams.get('slug');

  // API URL setup like DailyCircusPage
  let apiUrl = "https://api.gimmefy.ai/v2";

  const baseUrl = window.location.origin;
  if (baseUrl.includes("localhost") || baseUrl.includes("dev.gimmefy.ai")) {
    apiUrl = "https://apidev.gimmefy.ai/v2";
  } else if (baseUrl.includes("staging.gimmefy.ai")) {
    apiUrl = "https://apistaging.gimmefy.ai/v2";
  }

  // Fetch settings API
  const fetchSettings = async () => {
    try {
      const settingsUrl = `${apiUrl}/settings/public?filters[setting_name]=WEBSITE_VIDEO_GALLERY_SETTINGS`;
      const response = await fetch(settingsUrl);
      const data = await response.json();
      
      if (data.success && data.payload && data.payload.length > 0) {
        // Parse the JSON string from the value field
        const settingsValue = JSON.parse(data.payload[0].value);
        setSettings(settingsValue);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  // Fetch content API
  const fetchVideos = async () => {
    try {
      const contentUrl = `${apiUrl}/contents/public?filters[group_name]=WEBSITE_VIDEO_GALLERY&sort=display_order`;
      const response = await fetch(contentUrl);
      const data = await response.json();
      
      if (data.success && data.payload) {
        const transformedVideos: VideoItem[] = data.payload.map((item: Record<string, unknown>, index: number) => {
          const content2 = (item.content2 as string) || '';
          
          // Convert YouTube watch URL to embed URL if needed
          let youtubeId = content2;
          if (content2.includes('youtube.com/watch?v=')) {
            const videoId = content2.split('v=')[1]?.split('&')[0];
            if (videoId) {
              youtubeId = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&autoplay=1`;
            }
          } else if (content2.includes('youtu.be/')) {
            // Handle youtu.be short URLs
            const videoId = content2.split('youtu.be/')[1]?.split('?')[0];
            if (videoId) {
              youtubeId = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&autoplay=1`;
            }
          } else if (content2.includes('youtube.com/shorts/')) {
            // Handle YouTube Shorts URLs
            const videoId = content2.split('shorts/')[1]?.split('?')[0];
            if (videoId) {
              youtubeId = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&autoplay=1`;
            }
          } else if (!content2.includes('/embed/')) {
            // If it's not an embed URL and not a watch URL, assume it's a video ID
            youtubeId = `https://www.youtube-nocookie.com/embed/${content2}?rel=0&modestbranding=1&controls=1&autoplay=1`;
          }
          
          return {
            id: (item.id as string) || `video-${index + 1}`,
            title: (item.name as string) || 'Untitled Video',
            description: (item.content as string) || 'No description available',
            youtubeId: youtubeId,
            category: (item.categories as string) || 'General',
            tags: (item.tags as string)?.split(',').map(tag => tag.trim()) || [],
            url: youtubeId,
            url_slug: (item.url_slug as string) || `video-${index + 1}`
          };
        });
        setApiVideos(transformedVideos);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setIsLoadingVideos(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchVideos();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle slug parameter to open specific video
  useEffect(() => {
    if (slugParam && apiVideos.length > 0) {
      const videoBySlug = apiVideos.find(video => {
        return video.url_slug === slugParam;
      });
      
      if (videoBySlug) {
        // Open the video popup - this will be handled by the TabbedVideoGallery component
        // We'll pass the slug to the component and let it handle the popup opening
      }
    }
  }, [slugParam, apiVideos]);

  // Fallback data
  const workflowVideos = [
    {
      id: '1',
      title: 'Meet Studio: Your All-in-One Marketing Workspace',
      description: 'Get a complete walkthrough of gimmefy Studio and discover how it transforms your marketing workflow with AI-powered tools.',
      youtubeId: 'FtKSGbriRok',
      category: 'Getting Started',
      tags: ['Video Creation', 'Content Creation', 'Productivity']
    },
    {
      id: '2', 
      title: 'Brand Vault: Keep Every Word On-Brand',
      description: 'Learn how gimmefy\'s Brand Vault ensures consistent messaging and maintains your brand voice across all content.',
      youtubeId: 'QgLk1sOFxOc',
      category: 'Brand Setup',
      tags: ['Brand Management', 'Content Creation', 'Productivity']
    },
    {
      id: '3',
      title: 'AutoBrief Magic: Fill Marketing Briefs in Seconds',
      description: 'Discover how to create comprehensive marketing briefs instantly using gimmefy\'s intelligent AutoBrief feature.',
      youtubeId: 'AGyOSzuBA-8',
      category: 'Content Creation',
      tags: ['Briefs', 'Content Creation', 'Productivity']
    },
    {
      id: '4',
      title: 'Visual Editor: Design Standout Creatives',
      description: 'Master the Visual Editor to create professional marketing creatives without any design experience required.',
      youtubeId: '9VeFa4NW4yE',
      category: 'Visual Design',
      tags: ['Design', 'Content Creation', 'Editing']
    },
    {
      id: '5',
      title: 'Meet the Agents: Specialist AIs for Marketing',
      description: 'Explore gimmefy\'s AI Agents and learn how they accelerate your marketing tasks with specialized intelligence.',
      youtubeId: 'PNh1eHEZwu8',
      category: 'AI Features',
      tags: ['AI Agents', 'Productivity', 'Content Creation']
    },
    {
      id: '6',
      title: 'Edit, Polish & Publish: Complete Workflow',
      description: 'Learn the complete creative workflow from ideation to publication using gimmefy\'s integrated workspace.',
      youtubeId: 'rlhfQEp6xtY',
      category: 'Publishing',
      tags: ['Editing', 'Publishing', 'Design']
    }
  ];

  const coolFeatureVideos = [
    {
      id: '7',
      title: 'Marketing Content That Speaks Like Your Brand',
      description: 'Discover how to create marketing content that perfectly matches your brand voice and personality.',
      youtubeId: '7IDpj7lQ9x0',
      category: 'Brand Voice',
      tags: ['Brand Voice', 'Audience']
    },
    {
      id: '8',
      title: 'Be a Prompting Genius with Magic Button: Auto Generate AI Prompts',
      description: 'Learn how to use the Magic Button to automatically generate powerful AI prompts for better results.',
      youtubeId: 'cZ2lqZe6E-s',
      category: 'AI Prompts',
      tags: ['AI Prompts', 'Editing Tools']
    },
    {
      id: '9',
      title: 'Save Your Result as a PDF with Result Download',
      description: 'Export your content as professional PDFs for easy sharing and presentation.',
      youtubeId: 'XuqhDRBhr-w',
      category: 'Export',
      tags: ['Export']
    },
    {
      id: '10',
      title: 'Download and Save Your Brand Vault as a PDF',
      description: 'Keep your brand guidelines organized by downloading your Brand Vault as a comprehensive PDF.',
      youtubeId: 'TLWS30O-IYc',
      category: 'Brand Management',
      tags: ['Export', 'Brand Voice']
    },
    {
      id: '11',
      title: 'Tweak Your Result\'s Tone of Voice with ReWrite: Tonality',
      description: 'Adjust your content\'s tone and voice to match different audiences and contexts.',
      youtubeId: 'Nedf6U4cMEM',
      category: 'Content Editing',
      tags: ['Editing Tools', 'Brand Voice']
    },
    {
      id: '12',
      title: 'Make Your Content Region-Specific with ReWrite: Localisation',
      description: 'Adapt your content for different regions and cultural contexts with smart localization.',
      youtubeId: '7kotpm3NM14',
      category: 'Localization',
      tags: ['Localisation', 'Editing Tools']
    },
    {
      id: '13',
      title: 'Transcreate Your Content in 40+ Languages with ReWrite: Language',
      description: 'Expand your reach by translating and adapting content for global audiences.',
      youtubeId: 'iSUa6Y8coeo',
      category: 'Translation',
      tags: ['Languages', 'Editing Tools']
    },
    {
      id: '14',
      title: 'Modify Your Content\'s Length Easily with ReWrite: Length',
      description: 'Quickly adjust content length to fit different platforms and requirements.',
      youtubeId: 'jAMSPavIcgg',
      category: 'Content Editing',
      tags: ['Editing Tools']
    },
    {
      id: '15',
      title: 'Add Themes to Your Content with Review & Refine: Variations',
      description: 'Explore different creative themes and variations for your content.',
      youtubeId: 'w-RbriCavQk',
      category: 'Content Variations',
      tags: ['Editing Tools', 'Structure']
    },
    {
      id: '16',
      title: 'Give Structure to Your Content with Review & Refine: Story Arc',
      description: 'Create compelling narratives with professional story arc frameworks.',
      youtubeId: 'N9nPd6KCUAE',
      category: 'Story Structure',
      tags: ['Structure']
    },
    {
      id: '17',
      title: 'Create Pro-Level Content with Review & Refine: Expert\'s Take',
      description: 'Get expert-level insights and improvements for your marketing content.',
      youtubeId: 'ujXfFI2CWsg',
      category: 'Expert Review',
      tags: ['Expert Tips', 'Editing Tools']
    },
    {
      id: '18',
      title: 'Tailor Your Content with Review & Refine: Audience Focus',
      description: 'Optimize your content for specific target audiences and demographics.',
      youtubeId: 'k5rXYsV_vjQ',
      category: 'Audience Targeting',
      tags: ['Audience', 'Editing Tools']
    },
    {
      id: '19',
      title: 'Predict Your Content\'s Feedback with Review & Refine: Feedback Preview',
      description: 'Get insights into how your audience might respond to your content before publishing.',
      youtubeId: '2RCH0p40XA0',
      category: 'Feedback Analysis',
      tags: ['Feedback', 'Editing Tools']
    }
  ];

  // Filter videos by category based on settings
  const getVideosByCategory = (categoryName: string) => {
    if (!settings || !settings.categories || apiVideos.length === 0) {
      // Return fallback data if API data not available
      return categoryName === 'Workflow Essentials' ? workflowVideos : coolFeatureVideos;
    }

    const category = settings.categories.find(cat => cat.name === categoryName);
    if (!category) {
      // If category not found in settings, return all videos for that category from API
      return apiVideos.filter(video => video.category === categoryName);
    }

    return apiVideos.filter(video => 
      category.tags.some(tag => video.tags.includes(tag))
    );
  };

  // Get category names from settings
  const getCategoryNames = () => {
    if (!settings || !settings.categories) {
      return ['Workflow Essentials', 'Cool Features'];
    }
    return settings.categories.map(cat => cat.name);
  };

  const categoryNames = getCategoryNames();
  const workflowVideosToUse = getVideosByCategory(categoryNames[0] || 'Workflow Essentials');
  const coolFeatureVideosToUse = getVideosByCategory(categoryNames[1] || 'Cool Features');

  return (
    <>
      {seoData && (
        <SEO 
          data={{
            ...seoData,
            ...(getDynamicVideoData() || {})
          }} 
          canonicalUrl={canonicalUrl}
        />
      )}
      
      <div className="min-h-screen bg-background overflow-y-auto">
        <Navigation />
        
        <main>
          {/* Enhanced Hero Section */}
          <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-blue-500/5">
            {/* Enhanced Glow Effects */}
            <Glow variant="above" className="opacity-20" />
            <Glow variant="center" className="opacity-10" />
            
            <div className="pb-4 pt-8 md:pb-6 lg:pb-8 lg:pt-16 relative overflow-hidden">
              {/* Enhanced floating shapes background with diverse colors */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <motion.div 
                  className="absolute top-16 left-[20%] w-32 h-32 bg-blue-500/20 rounded-full" 
                  animate={{ 
                    scale: [1, 1.1, 1],
                    opacity: [0.2, 0.4, 0.2],
                    rotate: [0, 180, 360]
                  }}
                  transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div 
                  className="absolute top-32 right-[20%] w-20 h-20 bg-teal-500/15 rotate-45" 
                  animate={{ 
                    y: [0, -20, 0],
                    rotate: [45, 225, 45],
                    opacity: [0.4, 0.8, 0.4]
                  }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                />
                <motion.div 
                  className="absolute bottom-32 left-[15%] w-24 h-40 bg-orange-500/12 rounded-lg" 
                  animate={{ 
                    x: [0, 15, 0],
                    scale: [1, 1.05, 1],
                    opacity: [0.3, 0.7, 0.3]
                  }}
                  transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 3 }}
                />
                <motion.div 
                  className="absolute top-1/3 right-[30%] w-16 h-12 bg-emerald-500/15 rounded-full" 
                  animate={{ 
                    scale: [1, 1.15, 1],
                    opacity: [0.2, 0.4, 0.2]
                  }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                />
                <motion.div 
                  className="absolute bottom-1/4 right-[15%] w-28 h-28 bg-cyan-500/10 rounded-xl" 
                  animate={{ 
                    rotate: [0, 90, 180, 270, 360],
                    scale: [1, 1.1, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 4 }}
                />
              </div>
              
              <div className="mx-auto max-w-7xl px-6 relative z-10 text-center">
                <motion.h1 
                  className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-5xl xl:text-6xl"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                  Getting Started with <br />
                  <span className="bg-gradient-to-r from-blue-600 via-teal-500 to-emerald-600 bg-clip-text text-transparent">gimmefy</span>
                </motion.h1>
                
                <motion.p 
                  className="mt-6 text-lg text-muted-foreground lg:text-xl leading-relaxed max-w-4xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                >
                  Watch our step-by-step video guides to master gimmefy's AI-powered marketing platform and streamline your entire content creation journey.
                </motion.p>
                
                <motion.p 
                  className="mt-4 text-lg font-bold lg:text-xl text-foreground max-w-4xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
                >
                  From first spark to final post—watch the full workflow in action, or discover the features that set gimmefy apart.
                </motion.p>
              </div>
            </div>
            
            {/* White fade overlay for seamless transition without affecting layout */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-background z-0" />
          </section>

          {/* Video Gallery Section */}
          <section id="video-gallery" className="py-16 px-4 bg-gradient-to-b from-background to-slate-50/50 dark:to-slate-900/30">
            <div className="container mx-auto">
              <TabbedVideoGallery 
                workflowVideos={workflowVideosToUse} 
                coolFeatureVideos={coolFeatureVideosToUse} 
                settings={settings}
                isLoading={isLoadingVideos || isLoadingSettings}
                slugParam={slugParam}
                navigate={navigate}
              />
            </div>
          </section>
        </main>
        
        <FooterWithNewsletter />
      </div>
    </>
  );
};

export default GettingStartedPage;